import {
  isTerminalWriter,
  type CallParticipantRole,
  type CallTerminalOutcome,
} from './callPolicy';

export type { CallTerminalOutcome } from './callPolicy';

export type CallPhase =
  | 'idle'
  | 'incoming-ringing'
  | 'outgoing-ringing'
  | 'connecting'
  | 'active'
  | 'reconnecting'
  | 'ending'
  | 'failed';

export type CallFailureCategory =
  | 'terminal-send'
  | 'livekit-disconnect'
  | 'matrixrtc-leave'
  | 'rtc-setup';

export interface CallTerminalRecord {
  callId: string;
  outcome: CallTerminalOutcome;
  initiator: string;
  startedTs: number;
  answeredTs?: number;
  endedTs: number;
  durationSec?: number;
}

export interface CurrentCall {
  callId: string;
  roomId: string;
  initiatorId: string;
  peerId: string;
  operatorId: number;
  direction: 'incoming' | 'outgoing';
  startedTs: number;
  answeredTs?: number;
  remoteMembershipReady?: boolean;
  remoteMembershipEventIds?: string[];
  mediaReady?: boolean;
  mediaReadyTs?: number;
  terminalClaimed?: boolean;
  operationNumber: number;
  ringEventId?: string;
}

export type LogicalCallRecord = CallTerminalRecord & {
  eventId: string;
  originServerTs: number;
};

export interface CallTerminalPresentation {
  callId: string;
  roomId: string;
  operationNumber: number;
  direction: 'incoming' | 'outgoing';
  outcome: CallTerminalOutcome;
}

export interface CallState {
  phase: CallPhase;
  operationNumber: number;
  current?: CurrentCall;
  records: LogicalCallRecord[];
  processedEventIds: string[];
  processedCallIds: string[];
  lastFailure?: CallFailureCategory;
  terminalPresentation?: CallTerminalPresentation;
}

export type CallEffect =
  | {
      type: 'start-timeout';
      callId: string;
      operationNumber: number;
      delayMs: number;
    }
  | { type: 'clear-timeout' }
  | {
      type: 'write-terminal';
      roomId: string;
      record: CallTerminalRecord;
    }
  | { type: 'end-local-audio'; roomId: string; operationNumber: number }
  | { type: 'finish-call'; roomId: string; operationNumber: number };

interface StartAction {
  callId: string;
  roomId: string;
  initiatorId: string;
  peerId: string;
  operatorId: number;
  startedTs: number;
}

export type CallAction =
  | { type: 'operation-reserved'; operationNumber: number }
  | ({ type: 'outgoing-started'; operationNumber: number } & StartAction)
  | ({
      type: 'incoming-ring';
      eventId: string;
      originServerTs: number;
    } & StartAction)
  | { type: 'accept'; operationNumber: number; at: number }
  | { type: 'connected'; operationNumber: number; at: number }
  | { type: 'reconnecting'; operationNumber: number }
  | { type: 'recovered'; operationNumber: number }
  | {
      type: 'membership-observed';
      callId: string;
      senderId: string;
      eventId: string;
      operationNumber: number;
    }
  | {
      type: 'peer-membership-lost';
      callId: string;
      senderId: string;
      operationNumber: number;
      at: number;
    }
  | {
      type: 'timeout';
      callId: string;
      operationNumber: number;
      at: number;
    }
  | {
      type: 'decline' | 'cancel' | 'hangup' | 'fail' | 'complete';
      operationNumber: number;
      at: number;
    }
  | {
      type: 'terminal-observed';
      roomId: string;
      senderId: string;
      localUserId: string;
      eventId: string;
      originServerTs: number;
      record: CallTerminalRecord;
    }
  | {
      type: 'effect-failed';
      failure: CallFailureCategory;
      operationNumber: number;
    }
  | { type: 'dismiss-terminal' }
  | { type: 'cleanup'; operationNumber: number };

export interface CallReduction {
  state: CallState;
  effects: CallEffect[];
}

export const callRingTimeoutMs = 30_000;

export const initialCallState: CallState = {
  phase: 'idle',
  operationNumber: 0,
  records: [],
  processedEventIds: [],
  processedCallIds: [],
};

const nonIdlePhases = new Set<CallPhase>([
  'incoming-ringing',
  'outgoing-ringing',
  'connecting',
  'active',
  'reconnecting',
  'ending',
  'failed',
]);

const rememberEvent = (eventIds: string[], eventId: string) =>
  [...eventIds, eventId].slice(-256);

const rememberCall = (callIds: string[], callId: string) =>
  [...callIds.filter((value) => value !== callId), callId].slice(-256);

const isEarlierTerminal = (
  candidate: LogicalCallRecord,
  current: LogicalCallRecord,
) =>
  candidate.originServerTs < current.originServerTs ||
  (candidate.originServerTs === current.originServerTs &&
    candidate.eventId < current.eventId);

const isValidTerminalWriter = (
  current: CurrentCall,
  senderId: string,
  localUserId: string,
  outcome: CallTerminalOutcome,
) => {
  const receiverId =
    current.direction === 'outgoing' ? current.peerId : localUserId;
  const role: CallParticipantRole | undefined =
    senderId === current.initiatorId
      ? 'initiator'
      : senderId === receiverId
        ? 'receiver'
        : undefined;
  return role !== undefined && isTerminalWriter(outcome, role);
};

const terminalFor = (
  current: CurrentCall,
  outcome: CallTerminalOutcome,
  at: number,
): CallTerminalRecord => ({
  callId: current.callId,
  outcome,
  initiator: current.initiatorId,
  startedTs: current.startedTs,
  ...(current.answeredTs === undefined
    ? {}
    : { answeredTs: current.answeredTs }),
  endedTs: at,
  ...(current.answeredTs === undefined
    ? {}
    : {
        durationSec: Math.max(0, Math.floor((at - current.answeredTs) / 1000)),
      }),
});

export const reduceCall = (
  state: CallState,
  action: CallAction,
): CallReduction => {
  if (action.type === 'effect-failed') {
    const failureIsCurrent = state.current
      ? state.current.operationNumber === action.operationNumber
      : state.operationNumber === action.operationNumber ||
        state.operationNumber === action.operationNumber + 1;
    if (!failureIsCurrent) return { state, effects: [] };
    return { state: { ...state, lastFailure: action.failure }, effects: [] };
  }
  if (action.type === 'operation-reserved') {
    if (
      state.phase !== 'idle' ||
      action.operationNumber !== state.operationNumber + 1
    ) {
      return { state, effects: [] };
    }
    return {
      state: {
        ...state,
        operationNumber: action.operationNumber,
        lastFailure: undefined,
      },
      effects: [],
    };
  }
  if (action.type === 'dismiss-terminal') {
    return state.phase === 'idle'
      ? {
          state: { ...state, terminalPresentation: undefined },
          effects: [],
        }
      : { state, effects: [] };
  }
  if (action.type === 'outgoing-started') {
    if (state.phase !== 'idle') return { state, effects: [] };
    return {
      state: {
        ...state,
        phase: 'outgoing-ringing',
        operationNumber: action.operationNumber,
        current: { ...action, direction: 'outgoing' },
        lastFailure: undefined,
        terminalPresentation: undefined,
        processedCallIds: rememberCall(state.processedCallIds, action.callId),
      },
      effects: [
        {
          type: 'start-timeout',
          callId: action.callId,
          operationNumber: action.operationNumber,
          delayMs: callRingTimeoutMs,
        },
      ],
    };
  }
  if (action.type === 'incoming-ring') {
    if (state.processedEventIds.includes(action.eventId)) {
      return { state, effects: [] };
    }
    if (state.processedCallIds.includes(action.callId)) {
      return {
        state: {
          ...state,
          processedEventIds: rememberEvent(
            state.processedEventIds,
            action.eventId,
          ),
        },
        effects: [],
      };
    }
    if (nonIdlePhases.has(state.phase)) {
      return {
        state: {
          ...state,
          processedEventIds: rememberEvent(
            state.processedEventIds,
            action.eventId,
          ),
          processedCallIds: rememberCall(state.processedCallIds, action.callId),
        },
        effects: [
          {
            type: 'write-terminal',
            roomId: action.roomId,
            record: {
              callId: action.callId,
              outcome: 'busy',
              initiator: action.initiatorId,
              startedTs: action.startedTs,
              endedTs: action.originServerTs,
            },
          },
        ],
      };
    }
    const operationNumber = state.operationNumber + 1;
    return {
      state: {
        ...state,
        phase: 'incoming-ringing',
        operationNumber,
        current: {
          ...action,
          direction: 'incoming',
          operationNumber,
          ringEventId: action.eventId,
        },
        lastFailure: undefined,
        terminalPresentation: undefined,
        processedEventIds: rememberEvent(
          state.processedEventIds,
          action.eventId,
        ),
        processedCallIds: rememberCall(state.processedCallIds, action.callId),
      },
      effects: [],
    };
  }
  if (action.type === 'terminal-observed') {
    if (state.processedEventIds.includes(action.eventId)) {
      return { state, effects: [] };
    }
    const candidate = {
      ...action.record,
      eventId: action.eventId,
      originServerTs: action.originServerTs,
    };
    const current = state.current;
    if (
      current?.callId === action.record.callId &&
      (action.roomId !== current.roomId ||
        action.record.initiator !== current.initiatorId ||
        !isValidTerminalWriter(
          current,
          action.senderId,
          action.localUserId,
          action.record.outcome,
        ))
    ) {
      return {
        state: {
          ...state,
          processedEventIds: rememberEvent(
            state.processedEventIds,
            action.eventId,
          ),
        },
        effects: [],
      };
    }
    const existing = state.records.find(
      (record) => record.callId === action.record.callId,
    );
    const records = existing
      ? state.records.map((record) =>
          record.callId === action.record.callId &&
          isEarlierTerminal(candidate, record)
            ? candidate
            : record,
        )
      : [...state.records, candidate];
    const canonicalRecord = records.find(
      (record) => record.callId === action.record.callId,
    )!;
    const isCurrent = current?.callId === action.record.callId;
    const terminalPresentation = isCurrent
      ? {
          callId: current.callId,
          roomId: current.roomId,
          operationNumber: current.operationNumber,
          direction: current.direction,
          outcome: canonicalRecord.outcome,
        }
      : state.terminalPresentation?.callId === action.record.callId
        ? { ...state.terminalPresentation, outcome: canonicalRecord.outcome }
        : undefined;
    return {
      state: {
        ...state,
        ...(isCurrent ? { phase: 'ending' as const } : {}),
        ...(terminalPresentation ? { terminalPresentation } : {}),
        records,
        processedEventIds: rememberEvent(
          state.processedEventIds,
          action.eventId,
        ),
        processedCallIds: rememberCall(
          state.processedCallIds,
          action.record.callId,
        ),
      },
      effects: isCurrent
        ? [
            { type: 'clear-timeout' },
            {
              type: 'finish-call',
              roomId: current.roomId,
              operationNumber: current.operationNumber,
            },
          ]
        : [],
    };
  }
  const current = state.current;
  if (!current || action.operationNumber !== current.operationNumber) {
    return { state, effects: [] };
  }
  if (action.type === 'peer-membership-lost') {
    if (
      action.callId !== current.callId ||
      action.senderId !== current.peerId
    ) {
      return { state, effects: [] };
    }
    if (current.terminalClaimed) {
      return current.direction === 'incoming' && state.phase === 'ending'
        ? {
            state,
            effects: [
              {
                type: 'finish-call',
                roomId: current.roomId,
                operationNumber: current.operationNumber,
              },
            ],
          }
        : { state, effects: [] };
    }
    if (state.phase === 'outgoing-ringing' || state.phase === 'connecting') {
      if (current.remoteMembershipReady !== true) {
        return { state, effects: [] };
      }
      return {
        state: {
          ...state,
          phase: 'failed',
          current: {
            ...current,
            remoteMembershipReady: false,
            terminalClaimed: true,
          },
          terminalPresentation: {
            callId: current.callId,
            roomId: current.roomId,
            operationNumber: current.operationNumber,
            direction: current.direction,
            outcome: 'failed',
          },
        },
        effects: [
          { type: 'clear-timeout' },
          ...(current.direction === 'outgoing'
            ? [
                {
                  type: 'write-terminal' as const,
                  roomId: current.roomId,
                  record: terminalFor(current, 'failed', action.at),
                },
              ]
            : []),
          {
            type: 'finish-call',
            roomId: current.roomId,
            operationNumber: current.operationNumber,
          },
        ],
      };
    }
    if (state.phase !== 'active' && state.phase !== 'reconnecting') {
      return { state, effects: [] };
    }
    const outcome: CallTerminalOutcome = 'failed';
    return {
      state: {
        ...state,
        phase: 'failed',
        current: {
          ...current,
          remoteMembershipReady: false,
          terminalClaimed: true,
        },
        terminalPresentation: {
          callId: current.callId,
          roomId: current.roomId,
          operationNumber: current.operationNumber,
          direction: current.direction,
          outcome,
        },
      },
      effects: [
        { type: 'clear-timeout' },
        ...(current.direction === 'outgoing'
          ? [
              {
                type: 'write-terminal' as const,
                roomId: current.roomId,
                record: terminalFor(current, outcome, action.at),
              },
            ]
          : []),
        {
          type: 'finish-call',
          roomId: current.roomId,
          operationNumber: current.operationNumber,
        },
      ],
    };
  }
  if (action.type === 'membership-observed') {
    if (
      state.processedEventIds.includes(action.eventId) ||
      action.callId !== current.callId ||
      action.senderId !== current.peerId
    ) {
      return { state, effects: [] };
    }
    const ready =
      current.mediaReady === true &&
      (state.phase === 'connecting' || state.phase === 'outgoing-ringing');
    return {
      state: {
        ...state,
        phase: ready ? 'active' : state.phase,
        current: {
          ...current,
          remoteMembershipReady: true,
          remoteMembershipEventIds: rememberEvent(
            current.remoteMembershipEventIds ?? [],
            action.eventId,
          ),
          ...(ready
            ? { answeredTs: current.answeredTs ?? current.mediaReadyTs }
            : {}),
        },
        processedEventIds: rememberEvent(
          state.processedEventIds,
          action.eventId,
        ),
      },
      effects: ready ? [{ type: 'clear-timeout' }] : [],
    };
  }
  if (action.type === 'accept' && state.phase === 'incoming-ringing') {
    return {
      state: {
        ...state,
        phase: 'connecting',
        current: { ...current, answeredTs: action.at },
      },
      effects: [],
    };
  }
  if (
    action.type === 'connected' &&
    (state.phase === 'connecting' || state.phase === 'outgoing-ringing')
  ) {
    const ready = current.remoteMembershipReady === true;
    return {
      state: {
        ...state,
        phase: ready ? 'active' : state.phase,
        current: {
          ...current,
          mediaReady: true,
          mediaReadyTs: action.at,
          ...(ready ? { answeredTs: current.answeredTs ?? action.at } : {}),
        },
      },
      effects: ready ? [{ type: 'clear-timeout' }] : [],
    };
  }
  if (action.type === 'reconnecting' && state.phase === 'active') {
    return { state: { ...state, phase: 'reconnecting' }, effects: [] };
  }
  if (action.type === 'recovered' && state.phase === 'reconnecting') {
    return { state: { ...state, phase: 'active' }, effects: [] };
  }
  if (action.type === 'cleanup') {
    return {
      state: {
        ...state,
        phase: 'idle',
        current: undefined,
        operationNumber: state.operationNumber + 1,
      },
      effects: [{ type: 'clear-timeout' }],
    };
  }
  if (action.type === 'timeout') {
    if (
      state.phase !== 'outgoing-ringing' ||
      action.callId !== current.callId ||
      current.terminalClaimed
    ) {
      return { state, effects: [] };
    }
    return {
      state: {
        ...state,
        phase: 'ending',
        current: { ...current, terminalClaimed: true },
        terminalPresentation: {
          callId: current.callId,
          roomId: current.roomId,
          operationNumber: current.operationNumber,
          direction: current.direction,
          outcome: 'missed',
        },
      },
      effects: [
        { type: 'clear-timeout' },
        {
          type: 'write-terminal',
          roomId: current.roomId,
          record: terminalFor(current, 'missed', action.at),
        },
        {
          type: 'finish-call',
          roomId: current.roomId,
          operationNumber: current.operationNumber,
        },
      ],
    };
  }
  if (
    action.type !== 'decline' &&
    action.type !== 'cancel' &&
    action.type !== 'hangup' &&
    action.type !== 'fail' &&
    action.type !== 'complete'
  ) {
    return { state, effects: [] };
  }
  const validTerminalAction =
    (action.type === 'decline' && state.phase === 'incoming-ringing') ||
    (action.type === 'cancel' && state.phase === 'outgoing-ringing') ||
    ((action.type === 'hangup' || action.type === 'complete') &&
      (state.phase === 'active' || state.phase === 'reconnecting')) ||
    (action.type === 'fail' &&
      (state.phase === 'incoming-ringing' ||
        state.phase === 'outgoing-ringing' ||
        state.phase === 'connecting' ||
        state.phase === 'active' ||
        state.phase === 'reconnecting'));
  if (!validTerminalAction || current.terminalClaimed) {
    return { state, effects: [] };
  }
  const outcome: CallTerminalOutcome =
    action.type === 'decline'
      ? 'declined'
      : action.type === 'cancel'
        ? 'cancelled'
        : action.type === 'fail'
          ? 'failed'
          : 'completed';
  const localRole: CallParticipantRole =
    current.direction === 'outgoing' ? 'initiator' : 'receiver';
  const localIsWriter = isTerminalWriter(outcome, localRole);
  const awaitsInitiatorRecord =
    current.direction === 'incoming' &&
    action.type === 'hangup' &&
    outcome === 'completed';
  return {
    state: {
      ...state,
      phase: action.type === 'fail' ? 'failed' : 'ending',
      current: { ...current, terminalClaimed: true },
      terminalPresentation: {
        callId: current.callId,
        roomId: current.roomId,
        operationNumber: current.operationNumber,
        direction: current.direction,
        outcome,
      },
    },
    effects: [
      { type: 'clear-timeout' },
      ...(localIsWriter
        ? [
            {
              type: 'write-terminal' as const,
              roomId: current.roomId,
              record: terminalFor(current, outcome, action.at),
            },
          ]
        : []),
      awaitsInitiatorRecord
        ? {
            type: 'end-local-audio',
            roomId: current.roomId,
            operationNumber: current.operationNumber,
          }
        : {
            type: 'finish-call',
            roomId: current.roomId,
            operationNumber: current.operationNumber,
          },
    ],
  };
};
